import {Component, computed, DestroyRef, inject, linkedSignal, signal, viewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {ModalComponent} from '../../../../shared/components/modal/modal.component';
import {ToastrService} from 'ngx-toastr';
import {RuleCheckerService} from '../../../../services/store/rule-checker.service';
import {StoreWorkingTime} from '../../../../classes/models/store/StoreWorkingTime';
import {DataService} from '../../../../services/store/data.service';
import {StoreSpecialWorkingTime} from '../../../../classes/models/store/StoreSpecialWorkingTime';
import {StoreSpecialWorkingTimeService} from '../../../../services/http/store/store-special-working-time.service';
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";

/**
 * Defines all the data the form modal needs.
 */
export interface SpecificWorkingTimesFormModalData {
  storeId: string;
  previousSpecialWorkingTime?: StoreSpecialWorkingTime;
  weekExists: boolean;
}

/**
 * This component handles creating a new special working time.
 */
@Component({
  selector: 'app-specific-working-times-form',
  templateUrl: './specific-working-times-form.component.html',
  styleUrls: ['./specific-working-times-form.component.less'],
  imports: [SHARED_IMPORTS]
})
export class SpecificWorkingTimesFormComponent {

  /** A modal component of a special working time form. */
  readonly modalComponent = viewChild<ModalComponent>('modalComponent');
  /** An identifier of a store. */
  readonly storeId = signal<string>('');
  /** Previous special store working time. */
  readonly previousSpecialWorkingTime = signal<StoreSpecialWorkingTime | undefined>(undefined);
  /** `True` if there are records of the selected week in backend. */
  readonly weekExists = signal(false);

  /** A form signal for the date of a special working time, derived from previous special working time signal. */
  readonly date = linkedSignal<string | undefined>(() => this.previousSpecialWorkingTime()?.startDate);
  /** A form signal for the opening time, derived from previous special working time signal. */
  readonly openingTime = linkedSignal(() =>
          this.previousSpecialWorkingTime()?.workingTime?.openingTime ?? '00:00:00'
  );
  /** A form signal for the closing time, derived from previous special working time signal. */
  readonly closingTime = linkedSignal(() =>
          this.previousSpecialWorkingTime()?.workingTime?.closingTime ?? '00:00:00'
  );
  /** `True` if request to backend is in progress. */
  readonly loading = signal(false);
  /** The first date from which data can be edited. */
  readonly editDate = computed(() => this.dataService.editDate);
  /** Computed validity of the form based on signal values. */
  readonly isFormValid = computed(() =>
          !!this.date() && !!this.openingTime() && !!this.closingTime() && !this.loading()
  );

  private readonly destroyRef = inject(DestroyRef);
  private readonly storeSpecialWorkingTimeService = inject(StoreSpecialWorkingTimeService);
  private readonly toastr = inject(ToastrService);
  private readonly ruleCheckerService = inject(RuleCheckerService);
  private readonly dataService = inject(DataService);

  static open(modalService: NgbModal, data: SpecificWorkingTimesFormModalData): NgbModalRef {
    const modalRef = modalService.open(SpecificWorkingTimesFormComponent);
    const instance = modalRef.componentInstance as SpecificWorkingTimesFormComponent;
    instance.storeId.set(data.storeId);
    instance.previousSpecialWorkingTime.set(data.previousSpecialWorkingTime);
    instance.weekExists.set(data.weekExists);
    return modalRef;
  }

  /**
   * Sends request to backend to create or edit a special working time.
   */
  onSubmit(): void {
    this.loading.set(true);
    const workingTime = new StoreWorkingTime(this.openingTime(), this.closingTime());
    const specialWorkingTime = new StoreSpecialWorkingTime(this.date(), workingTime);

    if (this.weekExists()) {
      this.editSpecialStoreWorkingTime(specialWorkingTime);
    } else {
      this.addSpecialStoreWorkingTime(specialWorkingTime);
    }
  }

  private addSpecialStoreWorkingTime(specialWorkingTime: StoreSpecialWorkingTime): void {
    this.storeSpecialWorkingTimeService.addSpecialStoreWorkingTime(this.storeId(), specialWorkingTime)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.modalComponent()?.activeModal.close();
                this.loading.set(false);
                this.toastr.success('Store specific trading time successfully created');
                this.ruleCheckerService.check();
              },
              error: (error) => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }

  private editSpecialStoreWorkingTime(specialWorkingTime: StoreSpecialWorkingTime): void {
    this.storeSpecialWorkingTimeService.editSpecialStoreWorkingTime(this.storeId(), specialWorkingTime)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.modalComponent()?.activeModal.close();
                this.loading.set(false);
                this.toastr.success('Store specific trading time successfully edited');
                this.ruleCheckerService.check();
              },
              error: (error) => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }
}